import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './api';

export type ProjectTeamRole = 'admin' | 'lead' | 'member' | 'viewer';
export type ProjectTeamStatus = 'invited' | 'active';

export interface ProjectTeamMember {
  id: string;
  project_id: string;
  email: string;
  role: ProjectTeamRole;
  status: ProjectTeamStatus;
  invited_by?: string;
  is_owner: boolean;
  created_at: string;
  updated_at: string;
}

export interface ProjectCapabilities {
  role?: ProjectTeamRole;
  can_view: boolean;
  /** Post discussion comments: project roles that can execute, plus workspace editors and commentators. */
  can_comment?: boolean;
  can_execute: boolean;
  /** Sign off, resolve or reopen a discussion — project roles only, never through a workspace. */
  can_sign_off?: boolean;
  /** Record deployments, promote releases, request emergency changes — project roles only. */
  can_deploy?: boolean;
  can_delete_project?: boolean;
  can_approve: boolean;
  can_edit_policies: boolean;
  can_manage_team: boolean;
  is_member: boolean;
  bootstrap_open: boolean;
  /** Strongest accepted workspace role reaching this project, when access comes through a workspace. */
  workspace_role?: 'editor' | 'commentator' | 'viewer';
}

export interface ProjectTeamMe {
  email: string;
  member?: ProjectTeamMember | null;
  capabilities: ProjectCapabilities;
}

export function useProjectTeamMembers(projectId: string) {
  return useQuery({
    queryKey: ['project-team', projectId, 'members'],
    enabled: !!projectId,
    queryFn: ({ signal }) =>
      apiClient<{ members: ProjectTeamMember[] }>(`/projects/${projectId}/team/members`, { signal }),
    select: (data) => data.members ?? [],
  });
}

export function useProjectTeamMe(projectId: string) {
  return useQuery({
    queryKey: ['project-team', projectId, 'me'],
    enabled: !!projectId,
    queryFn: ({ signal }) => apiClient<ProjectTeamMe>(`/projects/${projectId}/team/me`, { signal }),
    staleTime: 15_000,
  });
}

/** Capability map for the current actor on a project (from GET .../team/me). */
export function useProjectCapabilities(projectId: string) {
  const me = useProjectTeamMe(projectId);
  return {
    ...me,
    caps: me.data?.capabilities,
  };
}

/** Maps team role → free-text governance approver_role vocabulary. */
export function governanceApproverRoleFor(role?: ProjectTeamRole): string {
  if (role === 'admin' || role === 'lead') return 'engineering-lead';
  return 'any';
}

export function useInviteProjectTeamMember(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { email: string; role: ProjectTeamRole }) =>
      apiClient<ProjectTeamMember>(`/projects/${projectId}/team/members`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['project-team', projectId] });
    },
  });
}

export function useUpdateProjectTeamMember(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { memberId: string; role?: ProjectTeamRole; status?: ProjectTeamStatus }) =>
      apiClient<ProjectTeamMember>(`/projects/${projectId}/team/members/${args.memberId}`, {
        method: 'PATCH',
        body: JSON.stringify({ role: args.role, status: args.status }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['project-team', projectId] });
    },
  });
}

export function useRemoveProjectTeamMember(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (memberId: string) =>
      apiClient<{ removed: boolean }>(`/projects/${projectId}/team/members/${memberId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['project-team', projectId] });
    },
  });
}

export function useTransferProjectOwner(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (memberId: string) =>
      apiClient<ProjectTeamMember>(`/projects/${projectId}/team/transfer-owner`, {
        method: 'POST',
        body: JSON.stringify({ member_id: memberId }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['project-team', projectId] });
    },
  });
}

export function useActivateProjectTeamSelf(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiClient<ProjectTeamMember>(`/projects/${projectId}/team/activate`, { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['project-team', projectId] });
    },
  });
}
